import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { profileService } from '@/services';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import type { Layout, LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

type Layouts = { [breakpoint: string]: LayoutItem[] };

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GripVertical, RotateCcw } from 'lucide-react';

import { DashboardEarlyPayCard } from '@/components/dashboard/DashboardEarlyPayCard';
import { DashboardNewInvoicesCard } from '@/components/dashboard/DashboardNewInvoicesCard';
import { DashboardAdHocUsageCard } from '@/components/dashboard/DashboardAdHocUsageCard';
import { DashboardTopAdHocItemsCard } from '@/components/dashboard/DashboardTopAdHocItemsCard';
import { DashboardComplianceCard } from '@/components/dashboard/DashboardComplianceCard';
import { DashboardAIComplianceCard } from '@/components/dashboard/DashboardAIComplianceCard';

const LAYOUT_STORAGE_KEY = 'dashboardLayout';

const DEFAULT_LAYOUTS: Layouts = {
  lg: [
    { i: 'earlyPay', x: 0, y: 0, w: 6, h: 12 },
    { i: 'compliance', x: 6, y: 0, w: 6, h: 12 },
    { i: 'aiCompliance', x: 0, y: 12, w: 6, h: 12 },
    { i: 'newInvoices', x: 0, y: 24, w: 4, h: 10 },
    { i: 'adHocUsage', x: 4, y: 24, w: 4, h: 10 },
    { i: 'topAdHocItems', x: 8, y: 24, w: 4, h: 10 },
  ],
  md: [
    { i: 'earlyPay', x: 0, y: 0, w: 5, h: 12 },
    { i: 'compliance', x: 5, y: 0, w: 5, h: 12 },
    { i: 'aiCompliance', x: 0, y: 12, w: 10, h: 12 },
    { i: 'newInvoices', x: 0, y: 24, w: 10, h: 10 },
    { i: 'adHocUsage', x: 0, y: 34, w: 10, h: 10 },
    { i: 'topAdHocItems', x: 0, y: 44, w: 10, h: 10 },
  ],
  sm: [
    { i: 'compliance', x: 0, y: 0, w: 6, h: 10 },
    { i: 'aiCompliance', x: 0, y: 10, w: 6, h: 10 },
    { i: 'earlyPay', x: 0, y: 20, w: 6, h: 12 },
    { i: 'newInvoices', x: 0, y: 32, w: 6, h: 10 },
    { i: 'adHocUsage', x: 0, y: 42, w: 6, h: 10 },
    { i: 'topAdHocItems', x: 0, y: 52, w: 6, h: 10 },
  ],
};

function DragHandle() {
  return (
    <div className="drag-handle absolute top-2 right-2 cursor-move p-1 rounded hover:bg-muted/50 z-10">
      <GripVertical className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { width, containerRef, mounted } = useContainerWidth();

  const [layouts, setLayouts] = useState<Layouts>(() => {
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_LAYOUTS;
      }
    }
    return DEFAULT_LAYOUTS;
  });

  const handleLayoutChange = useCallback((_currentLayout: Layout, allLayouts: Layouts) => {
    setLayouts(allLayouts);
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(allLayouts));
  }, []);

  const resetLayout = useCallback(() => {
    setLayouts(DEFAULT_LAYOUTS);
    localStorage.removeItem(LAYOUT_STORAGE_KEY);
  }, []);

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.getCurrentProfile();
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  return (
    <div className="animate-slide-up">
      {/* Page Header */}
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your contract compliance metrics.</p>
        </div>
        <Button variant="outline" size="sm" onClick={resetLayout}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset Layout
        </Button>
      </div>

      <div ref={containerRef}>
        {mounted && companyId && (
          <Responsive
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768 }}
            cols={{ lg: 12, md: 10, sm: 6 }}
            rowHeight={30}
            width={width}
            onLayoutChange={handleLayoutChange}
            dragConfig={{ enabled: true, handle: '.drag-handle' }}
            margin={[16, 16]}
            containerPadding={[0, 0]}
          >
            {/* Card 1: Early Pay Discounts */}
            <div key="earlyPay">
              <Card className="h-full relative overflow-hidden">
                <DragHandle />
                <DashboardEarlyPayCard companyId={companyId} />
              </Card>
            </div>

            {/* Card 2: Compliance Issues */}
            <div key="compliance">
              <Card className="h-full relative overflow-hidden flex flex-col">
                <DragHandle />
                <DashboardComplianceCard companyId={companyId} />
              </Card>
            </div>

            {/* Card 3: AI Compliance Engine */}
            <div key="aiCompliance">
              <Card className="h-full relative overflow-hidden flex flex-col">
                <DragHandle />
                <DashboardAIComplianceCard companyId={companyId} />
              </Card>
            </div>

            {/* Card 4: New Invoices */}
            <div key="newInvoices">
              <Card className="h-full relative overflow-hidden flex flex-col">
                <DragHandle />
                <DashboardNewInvoicesCard companyId={companyId} />
              </Card>
            </div>

            {/* Card 5: Ad-Hoc Usage by Vendor */}
            <div key="adHocUsage">
              <Card className="h-full relative overflow-hidden">
                <DragHandle />
                <DashboardAdHocUsageCard companyId={companyId} />
              </Card>
            </div>

            {/* Card 5: Top Ad-Hoc Items */}
            <div key="topAdHocItems">
              <Card className="h-full relative overflow-hidden flex flex-col">
                <DragHandle />
                <DashboardTopAdHocItemsCard companyId={companyId} />
              </Card>
            </div>
          </Responsive>
        )}
      </div>
    </div>
  );
}
